import { Autocomplete } from '@base-ui/react/autocomplete'
import type { Ref } from 'react'

import { useUserManagementEntityIds } from '@/modules/iam/user-management-entity-queries'
import {
  EntitySearchInput,
  EntitySearchResults,
  searchStatusText,
  useEntitySearchBox,
} from '@/modules/hierarchy'
import { useAssignableRoles } from '@/modules/iam/features/roles/role-queries'
import { Button } from '@/ui/button'
import { RolePicker } from '@/ui/role-picker'

export type RoleGrantDraft = {
  entityId: string
  entityName: string
  roleId: string | null
}

function EntityRoleGrantCard({
  grant,
  onSetRole,
  onRemove,
  invalid,
  describedBy,
  disabled,
}: {
  grant: RoleGrantDraft
  onSetRole: (roleId: string) => void
  onRemove: () => void
  invalid?: boolean
  describedBy?: string
  disabled?: boolean
}) {
  const roles = useAssignableRoles(grant.entityId)
  return (
    <div className="border-border flex flex-col gap-1.5 rounded-md border px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-foreground text-sm font-medium">{grant.entityName}</span>
        <Button type="button" variant="ghost" size="xs" disabled={disabled} onClick={onRemove}>
          Remove
        </Button>
      </div>
      <RolePicker
        roles={roles.data ?? []}
        isLoading={roles.isLoading}
        error={roles.isError ? roles.error : null}
        onRetry={() => void roles.refetch()}
        value={grant.roleId}
        onChange={onSetRole}
        disabled={disabled}
        aria-label={`Role at ${grant.entityName}`}
        aria-invalid={invalid}
        aria-describedby={describedBy}
      />
      {grant.roleId === null ? (
        <p className="text-muted-foreground text-[11px]">
          Select a role for {grant.entityName}, or remove it.
        </p>
      ) : null}
    </div>
  )
}

export function RoleGrantsField({
  entityId,
  entityName,
  grants,
  onAddEntity,
  onSetRole,
  onRemoveEntity,
  fieldRef,
  onBlur,
  invalid,
  errorId,
  error,
  disabled,
  restrictToManagementEntity = false,
}: {
  entityId: string
  entityName: string
  grants: RoleGrantDraft[]
  onAddEntity: (entity: { entityId: string; entityName: string }) => void
  onSetRole: (entityId: string, roleId: string) => void
  onRemoveEntity: (entityId: string) => void
  fieldRef?: Ref<HTMLFieldSetElement>
  onBlur?: () => void
  invalid?: boolean
  errorId?: string
  error?: string
  disabled?: boolean
  restrictToManagementEntity?: boolean
}) {
  const { query, setQuery, isSearching, search, results, pending } = useEntitySearchBox(entityId)
  const managementEntityIds = useUserManagementEntityIds(
    restrictToManagementEntity ? results.map((result) => result.entityId) : [],
  )
  const taken = new Set(grants.map((grant) => grant.entityId))
  const available = results.filter(
    (result) =>
      result.entityId !== entityId &&
      !taken.has(result.entityId) &&
      (!restrictToManagementEntity ||
        managementEntityIds.userManagementEntityIds.get(result.entityId) ===
          entityId),
  )
  const searchError = search.error
    ?? managementEntityIds.error
  const currentAdded = taken.has(entityId)
  const searchPending = pending
    || managementEntityIds.isPending

  return (
    <fieldset
      ref={fieldRef}
      tabIndex={-1}
      aria-describedby={error ? errorId : undefined}
      onBlur={onBlur}
      className="flex flex-col gap-1.5"
    >
      <legend className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
        Role grants
      </legend>
      {grants.map((grant) => (
        <EntityRoleGrantCard
          key={grant.entityId}
          grant={grant}
          onSetRole={(roleId) => onSetRole(grant.entityId, roleId)}
          onRemove={() => onRemoveEntity(grant.entityId)}
          invalid={invalid && grant.roleId === null}
          describedBy={error && grant.roleId === null ? errorId : undefined}
          disabled={disabled}
        />
      ))}

      {!currentAdded ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="self-start"
          onClick={() => onAddEntity({ entityId, entityName })}
        >
          + Add role at {entityName}
        </Button>
      ) : null}

      <Autocomplete.Root
        mode="none"
        open={!disabled && isSearching && searchError == null}
        onOpenChange={() => {}}
        value={query}
        onValueChange={(value) => { if (!disabled) setQuery(value) }}
      >
        <EntitySearchInput
          query={query}
          onValueChange={setQuery}
          disabled={disabled}
          onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault() }}
        />
        <Autocomplete.Status className="text-muted-foreground px-2 text-xs empty:sr-only">
          {searchStatusText(isSearching, searchPending, searchError != null, available.length)}
        </Autocomplete.Status>
        {!disabled && isSearching ? (
          <EntitySearchResults
            results={available}
            error={searchError}
            hasNextPage={search.data?.pageInfo.hasNextPage ?? false}
            selectedId={null}
            onSelect={(entity) => {
              onAddEntity({ entityId: entity.id, entityName: entity.name })
              setQuery('')
            }}
            onRetry={() => {
              void search.refetch()
              managementEntityIds.retry()
            }}
          />
        ) : null}
      </Autocomplete.Root>
      {error ? <p id={errorId} role="alert" className="text-destructive text-xs">{error}</p> : null}
    </fieldset>
  )
}
